import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { buildInventoryImageUrl } from '../../../utils/inventoryImage';
import { SkuFacetState, applyFacets } from './skuFacets';
import {
  MeasuredDimensions,
  formatMeasuredDimensions,
  formatWeight,
} from './skuHelpers';

export type ProductGroupListing = {
  product: InventorySKUListing['productDetails'];
  skus: InventorySKUListing[];
  skuCount: number;
  serverTruncated?: boolean;
};

export const productGroupKey = (group: ProductGroupListing): string =>
  group.skus[0]?.inventorySkuId || group.product?.productId || 'unknown';

export const productGroupName = (group: ProductGroupListing): string =>
  group.product?.name || group.skus[0]?.name || 'Unnamed product';

export const productThumbnailSrc = (
  group: ProductGroupListing
): string | null =>
  buildInventoryImageUrl(
    group.product?.image ??
      group.skus.find((sku) => sku.productDetails?.image)?.productDetails
        ?.image ??
      null
  );

export const productGroupId = (group: ProductGroupListing): string | null =>
  group.product?.productId ?? null;

export const productGroupWeight = (group: ProductGroupListing): string => {
  const firstSku = group.skus[0];
  return group.product?.weight != null
    ? formatWeight(group.product.weight, group.product.weightUnitOfMeasure)
    : formatWeight(firstSku?.grossWeight, firstSku?.grossWeightUnitOfMeasure);
};

const noMeasurements: MeasuredDimensions = {
  length: null,
  width: null,
  height: null,
  dimensionsUnitOfMeasure: null,
};

const hasEveryMeasurement = (
  dimensions: MeasuredDimensions | null | undefined
): boolean =>
  dimensions?.length != null &&
  dimensions.width != null &&
  dimensions.height != null;

export const productGroupDimensions = (group: ProductGroupListing): string => {
  const productDimensions = group.product?.dimensions ?? noMeasurements;
  return formatMeasuredDimensions(
    hasEveryMeasurement(productDimensions)
      ? productDimensions
      : group.skus[0]?.dimensions ?? noMeasurements
  );
};

export const productGroupTemperatureControl = (
  group: ProductGroupListing
): string =>
  group.skus.find(
    (sku) => sku.temperatureControlRequirements?.temperatureControlType
  )?.temperatureControlRequirements?.temperatureControlType ?? '—';

export const isGroupTruncatedOnServer = (group: ProductGroupListing): boolean =>
  group.serverTruncated ?? group.skus.length < group.skuCount;

export const representativeSkuOf = (
  group: ProductGroupListing
): InventorySKUListing => ({
  ...group.skus[0],
  productDetails: group.product ?? group.skus[0]?.productDetails ?? null,
});

export const applyFacetsToGroups = (
  groups: ProductGroupListing[],
  facets: SkuFacetState
): ProductGroupListing[] =>
  groups
    .map((group): ProductGroupListing | null => {
      const matchingSkus = applyFacets(group.skus, facets);
      return matchingSkus.length === 0
        ? null
        : {
            ...group,
            skus: matchingSkus,
            serverTruncated: isGroupTruncatedOnServer(group),
          };
    })
    .filter((group): group is ProductGroupListing => group !== null);

export const dedupeGroupsByKey = (
  groups: ProductGroupListing[]
): ProductGroupListing[] => {
  const seenKeys = new Set<string>();
  return groups.filter((group) => {
    const key = productGroupKey(group);
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
};

export const variantTotalProducts = (sku: InventorySKUListing): number =>
  (sku.stockByWarehouse ?? []).reduce(
    (total, warehouseStock) =>
      total +
      (warehouseStock.totalProducts ?? 0) +
      (warehouseStock.availableLooseProducts ?? 0) +
      (warehouseStock.allocatedLooseProducts ?? 0) +
      (warehouseStock.inOutboundTransitLooseProducts ?? 0),
    0
  );

export type ProductGroupUnitTotals = {
  totalProducts: number;
  availableProducts: number;
  allocatedProducts: number;
};

export const productGroupUnitTotals = (
  group: ProductGroupListing
): ProductGroupUnitTotals => {
  const totals: ProductGroupUnitTotals = {
    totalProducts: 0,
    availableProducts: 0,
    allocatedProducts: 0,
  };
  group.skus.forEach((sku) => {
    totals.totalProducts += variantTotalProducts(sku);
    (sku.stockByWarehouse ?? []).forEach((warehouseStock) => {
      totals.availableProducts +=
        (warehouseStock.availableProducts ?? 0) +
        (warehouseStock.availableLooseProducts ?? 0);
      totals.allocatedProducts +=
        (warehouseStock.allocatedProducts ?? 0) +
        (warehouseStock.allocatedLooseProducts ?? 0);
    });
  });
  return totals;
};
